import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Send, Paperclip, CheckCheck, Check, FileText, Play, Download, Phone, Mic, X, ZoomIn, ChevronDown, MessageCircle, Smile } from "lucide-react";
import { toast } from "./ui/sonner";
import { Dialog, DialogContent } from "./ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import EmojiPicker from "emoji-picker-react";
import { useAppearance } from "../contexts/AppearanceContext";
import { useAuth } from "../contexts/AuthContext";
import { applyMask } from "./PhoneInput";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── Helpers ──────────────────────────────────────────────────────────────── */

function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Hoje";
  if (d.toDateString() === yesterday.toDateString()) return "Ontem";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function looksLikePhone(str) {
  return str ? /^\+?[\d\s\-().]{7,}$/.test(str.trim()) : false;
}

function resolveInboundName(pushName, contactName) {
  if (!pushName) return contactName || "";
  if (looksLikePhone(pushName)) return contactName || pushName;
  return pushName;
}

function getInitials(name) {
  if (!name) return "?";
  return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
}

/* ─── Status tick ───────────────────────────────────────────────────────────── */

function StatusTick({ status, tc500 }) {
  if (status === "read")      return <CheckCheck className="w-3 h-3 flex-shrink-0" style={{ color: tc500 }} />;
  if (status === "delivered") return <CheckCheck className="w-3 h-3 text-slate-400 flex-shrink-0" />;
  return <Check className="w-3 h-3 text-slate-400 flex-shrink-0" />;
}

/* ─── Date separator ────────────────────────────────────────────────────────── */

function DateSeparator({ label }) {
  return (
    <div className="flex items-center gap-3 my-3">
      <div className="flex-1 h-px bg-slate-200" />
      <span className="text-[11px] font-medium text-slate-400 px-2">{label}</span>
      <div className="flex-1 h-px bg-slate-200" />
    </div>
  );
}

/* ─── Message Bubble ────────────────────────────────────────────────────────── */


function AudioPlayer({ src, tc500 }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); } else { audioRef.current.play(); }
    setPlaying(!playing);
  };

  const fmtTime = (s) => {
    if (!s || !isFinite(s) || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  return (
    <div className="flex items-center gap-2.5">
      <audio ref={audioRef} src={src} preload="metadata"
        onLoadedMetadata={e => { if (isFinite(e.target.duration)) setDuration(e.target.duration); }}
        onTimeUpdate={e => { setCurrentTime(e.target.currentTime); setProgress(e.target.duration && isFinite(e.target.duration) ? (e.target.currentTime / e.target.duration) * 100 : 0); }}
        onEnded={() => { setPlaying(false); setProgress(0); setCurrentTime(0); }}
      />
      <button onClick={toggle}
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
        style={{ backgroundColor: `${tc500}20`, color: tc500 }}>
        {playing ? (
          <svg width="12" height="12" viewBox="0 0 12 12"><rect x="1" y="1" width="3.5" height="10" rx="1" fill="currentColor"/><rect x="7.5" y="1" width="3.5" height="10" rx="1" fill="currentColor"/></svg>
        ) : (
          <svg width="12" height="12" viewBox="0 0 12 12"><path d="M2 1.5v9l8.5-4.5L2 1.5z" fill="currentColor"/></svg>
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div className="relative h-1.5 rounded-full overflow-hidden cursor-pointer"
          style={{ backgroundColor: "#e2e8f0" }}
          onClick={e => { if (!audioRef.current || !duration || !isFinite(duration)) return; const rect = e.currentTarget.getBoundingClientRect(); const pct = (e.clientX - rect.left) / rect.width; audioRef.current.currentTime = pct * duration; }}>
          <div className="h-full rounded-full transition-all duration-100" style={{ width: `${progress}%`, backgroundColor: tc500 }} />
        </div>
        <div className="flex justify-between mt-0.5">
          <span className="text-[10px] text-slate-400">
            {playing || currentTime > 0 ? fmtTime(currentTime) : (duration > 0 ? fmtTime(duration) : "")}
          </span>
          {duration > 0 && <span className="text-[10px] text-slate-400">{fmtTime(duration)}</span>}
        </div>
      </div>
    </div>
  );
}


function MessageBubble({ msg, contactName, tc500, tc50, localMedia, onImageClick }) {
  const isOut = msg.direction === "outbound";
  const senderLabel = null; // 1-to-1 chat: name already in header

  // localMedia = just-sent object URL; else use backend proxy for all stored media
  const authToken = typeof window !== "undefined" ? localStorage.getItem("session_token") || "" : "";
  const proxyUrl = `${API}/whatsapp/media/${msg.id}${authToken ? `?token=${authToken}` : ""}`;
  const mediaUrl = localMedia || (msg.media_url ? (msg.media_url.startsWith("data:") ? msg.media_url : proxyUrl) : (msg.evolution_id ? proxyUrl : ""));

  const bubbleStyle = isOut
    ? { backgroundColor: tc50 }
    : {};
  const bubbleClass = isOut
    ? "rounded-lg rounded-tl-lg border border-slate-200 shadow-sm"
    : "bg-white rounded-lg rounded-tr-lg border border-slate-200 shadow-sm";

  const bubblePadding = { paddingTop: "0.225rem", paddingBottom: "0.225rem", paddingLeft: "0.55rem", paddingRight: "0.55rem" };

  const renderContent = () => {
    const { message_type, text, display_text, filename } = msg;

    if (message_type === "sticker") {
      return mediaUrl ? (
        <div className="relative group cursor-zoom-in" onClick={() => onImageClick && onImageClick(proxyUrl)}>
          <img src={proxyUrl} alt="sticker"
            className="w-20 h-20 object-contain rounded-lg hover:opacity-90 transition-opacity"
            onError={e => {
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }} />
          <div className="hidden w-20 h-20 rounded-lg bg-slate-100 items-center justify-center text-2xl">🎭</div>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <span className="text-lg">🎭</span><span>Sticker</span>
        </div>
      );
    }

    if (message_type === "image") {
      return mediaUrl ? (
        <div className="cursor-zoom-in relative group" onClick={() => onImageClick && onImageClick(mediaUrl)}>
          <img src={mediaUrl} alt="imagem"
            className="max-w-[220px] rounded-lg mb-1 hover:opacity-90 transition-opacity"
            onError={e => e.target.style.display = 'none'} />
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            <div className="bg-black/30 rounded-full p-1.5">
              <ZoomIn className="w-4 h-4 text-white" />
            </div>
          </div>
          {text && <p className="text-slate-700" style={{ fontSize: "0.775rem" }}>{text}</p>}
        </div>
      ) : (
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Play className="w-4 h-4 flex-shrink-0" /><span>Imagem enviada</span>
        </div>
      );
    }

    if (message_type === "audio") {
      return (
        <div className="min-w-[220px] max-w-[280px]">
          {mediaUrl ? (
            <AudioPlayer src={mediaUrl} tc500={tc500} />
          ) : (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Mic className="w-4 h-4 flex-shrink-0" /><span>Áudio</span>
            </div>
          )}
          {msg.transcription && (
            <p className="text-[11px] italic mt-1.5 leading-relaxed text-slate-500">{msg.transcription}</p>
          )}
        </div>
      );
    }

    if (message_type === "video") {
      return mediaUrl ? (
        <div>
          <video src={mediaUrl} controls className="max-w-[220px] rounded-lg" />
          {text && <p className="text-sm text-slate-700 mt-1">{text}</p>}
        </div>
      ) : (
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Play className="w-4 h-4 flex-shrink-0" /><span>Vídeo enviado</span>
        </div>
      );
    }

    if (message_type === "document") {
      return (
        <div className="min-w-[190px]">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{ backgroundColor: tc500 }}>
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-0.5">Arquivo enviado</p>
              <p className="text-sm font-medium text-slate-800 break-all leading-snug">{filename || "Documento"}</p>
              {(mediaUrl || msg.media_url) && (
                <a href={mediaUrl || msg.media_url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 mt-1.5 text-[11px] font-medium hover:underline"
                  style={{ color: tc500 }}>
                  <Download className="w-3 h-3" />Baixar arquivo
                </a>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Text
    const content = display_text || text || "";
    const boldMatch = content.match(/^\*([^*]+):\*\n?([\s\S]*)$/);
    if (isOut && boldMatch) {
      return (
        <div className="text-slate-700" style={{ fontSize: "0.775rem" }}>
          <p className="font-semibold text-slate-800 leading-snug">{boldMatch[1]}:</p>
          <p className="whitespace-pre-wrap leading-snug">{boldMatch[2]}</p>
        </div>
      );
    }
    return <p className="text-slate-700 whitespace-pre-wrap" style={{ fontSize: "0.775rem" }}>{content}</p>;
  };

  return (
    <div className={`flex ${isOut ? "justify-end" : "justify-start"} mb-1`}>
      <div className={`max-w-[72%] ${bubbleClass}`} style={{ ...(isOut ? bubbleStyle : {}), ...bubblePadding }}>
        {senderLabel && (
          <p className="text-[10px] font-bold mb-0.5" style={{ color: tc500 }}>{senderLabel}</p>
        )}
        <div className="flex items-end gap-0">
          <div className="flex-1 min-w-0">{renderContent()}</div>
          <span className="text-[10px] text-slate-400 ml-2 flex-shrink-0 whitespace-nowrap self-end pb-px">{fmtTime(msg.created_at)}{isOut && <StatusTick status={msg.status} tc500={tc500} />}</span>
        </div>
      </div>
    </div>
  );
}

/* ─── Main component ────────────────────────────────────────────────────────── */

export default function WhatsAppChat({ negocio, contact, themeColor, leadId, onRead, hideHeader }) {
  const appearance = useAppearance();
  const { user: authUser } = useAuth();
  const { colors } = appearance;
  const tc500 = themeColor || colors?.["500"] || "#3B82F6";
  const tc50  = colors?.["50"]  || "#EFF6FF";
  const entityId = leadId || negocio?.id;
  const entityType = leadId ? "lead" : "negocio";

  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [emojiOpen, setEmojiOpen] = useState(false);
  const textareaRef = useRef(null);

  const EMOJI_SHORTCUTS = {
    ":)": "😊", ":-)": "😊", ":D": "😃", ":-D": "😃", ";)": "😉", ";-)": "😉",
    ":(": "😞", ":-(": "😞", ":P": "😛", ":-P": "😛", ":p": "😛", ":-p": "😛",
    "<3": "❤️", ":O": "😮", ":-O": "😮", ":o": "😮", "B)": "😎", ":/": "😕",
    "XD": "😆", "xD": "😆", ":*": "😘", ":-*": "😘", ":'(": "😢", "8)": "😎",
  };

  const handleTextChange = (val) => {
    let newVal = val;
    for (const [shortcut, emoji] of Object.entries(EMOJI_SHORTCUTS)) {
      // Only replace if shortcut is at end after a space or at start
      if (newVal.endsWith(shortcut + " ") || newVal.endsWith(shortcut)) {
        const idx = newVal.lastIndexOf(shortcut);
        if (idx === 0 || newVal[idx - 1] === " " || newVal[idx - 1] === "\n") {
          newVal = newVal.substring(0, idx) + emoji + newVal.substring(idx + shortcut.length);
        }
      }
    }
    setText(newVal);
  };
  const [sending, setSending] = useState(false);
  const [sendingFile, setSendingFile] = useState(false);
  const [availablePhones, setAvailablePhones] = useState([]);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [noPhone, setNoPhone] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncDone, setSyncDone] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [typingStatus, setTypingStatus] = useState(null);
  const [localMediaMap, setLocalMediaMap] = useState({});
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const localMediaRef = useRef({});

  // Derived: phone digits for sending
  const phone = selectedPhone
    ? (selectedPhone.digits.startsWith("55") ? selectedPhone.digits : "55" + selectedPhone.digits)
    : "";
  const phoneMask = appearance.phone_mask || "(##) #####-####";
  const defaultDdi = appearance.default_ddi || "55";

  /* Initial fetch — immediate, independent of sync */
  useEffect(() => {
    if (entityId && phone) {
      fetchMessages();
    }
  }, [entityId, phone]); // eslint-disable-line

  // Format phone for display: +DDI (##) #####-####
  const contactPhone = selectedPhone?.valor
    ? (() => {
        const ddi = (selectedPhone.ddi || defaultDdi).replace(/\D/g, "");
        let local = selectedPhone.valor.replace(/\D/g, "");
        // If local already starts with ddi, strip it
        if (local.startsWith(ddi) && local.length > 10) local = local.slice(ddi.length);
        return `+${ddi} ${applyMask(local, phoneMask)}`;
      })()
    : "";

  const fileInputRef = useRef(null);
  const bottomRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const pollRef = useRef(null);
  const seenMsgIds = useRef(new Set());
  const hasLoaded = useRef(false);
  const isAtBottomRef = useRef(true);
  const prevMsgCountRef = useRef(0);

  // Audio recording
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  const handleScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  /* Notification permission */
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  /* Cleanup object URLs on unmount */
  useEffect(() => {
    return () => {
      Object.values(localMediaRef.current).forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  /* Reset when negocio OR selected phone changes */
  useEffect(() => {
    seenMsgIds.current = new Set();
    hasLoaded.current = false;
    isAtBottomRef.current = true;
    prevMsgCountRef.current = 0;
    setMessages([]);
    setSyncDone(false);
  }, [negocio?.id, phone]);

  /* Resolve all available phones — auto-select the one with the most recent/unread message */
  useEffect(() => {
    if (!contact || !negocio?.id) return;
    const PHONE_TYPES = ["Celular", "WhatsApp", "Telefone", "Telefonico"];
    const all = (contact.tipos_contato || [])
      .filter(tc => PHONE_TYPES.includes(tc.tipo) && tc.valor)
      .map(tc => ({
        tipo: tc.tipo,
        valor: tc.valor,
        // Full number for WhatsApp = ddi + local digits
        digits: (() => {
          const ddi = (tc.ddi || "55").replace(/\D/g, "");
          const local = tc.valor.replace(/\D/g, "");
          // If local already starts with ddi, don't prepend
          if (local.startsWith(ddi) && local.length > 10) return local;
          return ddi + local;
        })()
      }));
    setAvailablePhones(all);
    if (all.length === 0) { setNoPhone(true); return; }
    setNoPhone(false);

    // Ask backend which phone has the latest (unread) message
    axios.get(`${API}/whatsapp/last-active-phone/${negocio.id}`, { withCredentials: true })
      .then(res => {
        const activePhone = res.data?.phone || "";
        if (activePhone) {
          const search = activePhone.replace(/\D/g, "").slice(-9);
          const match = all.find(p => p.digits.slice(-9) === search);
          if (match) { setSelectedPhone(match); return; }
        }
        // Fallback: prefer Celular (used for WhatsApp) then first
        setSelectedPhone(all.find(p => p.tipo === "Celular" || p.tipo === "Celular" || p.tipo === "WhatsApp") || all[0]);
      })
      .catch(() => {
        setSelectedPhone(all.find(p => p.tipo === "Celular" || p.tipo === "Celular" || p.tipo === "WhatsApp") || all[0]);
      });
  }, [contact, negocio?.id]);

  const fetchMessages = useCallback(async () => {
    if (!entityId || !phone) return;
    try {
      const msgEndpoint = entityType === "lead"
        ? `${API}/whatsapp/lead-messages/${entityId}?phone=${phone}`
        : `${API}/whatsapp/messages/${entityId}?phone=${phone}`;
      const presEndpoint = entityType === "lead" ? null : `${API}/whatsapp/presence/${entityId}`;
      const msgRes = await axios.get(msgEndpoint);
      const newMsgs = msgRes.data;
      if (hasLoaded.current) {
        newMsgs.forEach(msg => {
          if (!seenMsgIds.current.has(msg.id) && msg.direction === "inbound") {
            if ("Notification" in window && Notification.permission === "granted") {
              const name = msg.push_name || contact?.nome || "WhatsApp";
              const body = msg.text ? `${name}: ${msg.text.slice(0, 80)}` : `${name} enviou [${msg.message_type}]`;
              new Notification("Nova mensagem", { body, icon: "/favicon.ico" });
            }
          }
        });
      }
      newMsgs.forEach(m => seenMsgIds.current.add(m.id));
      hasLoaded.current = true;
      setMessages(newMsgs);
      // Only mark as read if there are new unread inbound messages
      const hasUnread = newMsgs.some(m => m.direction === "inbound" && !m.read);
      if (hasUnread) {
        const readEndpoint = entityType === "lead"
          ? `${API}/whatsapp/lead-messages/${entityId}/read?phone=${phone}`
          : `${API}/whatsapp/messages/${entityId}/read?phone=${phone}`;
        await axios.post(readEndpoint, {});
        if (onRead) onRead();
      }
    } catch { /* silent */ }
  }, [entityId, entityType, phone, contact?.nome]);

  /* Auto-sync history once per session per entity+phone (background, non-blocking) */
  useEffect(() => {
    if (!phone || syncDone) return;
    const id = entityType === "lead" ? entityId : negocio?.id;
    if (!id) return;
    const key = `wa_synced_${id}_${phone}`;
    if (sessionStorage.getItem(key)) { setSyncDone(true); return; }
    (async () => {
      setSyncing(true);
      try {
        const url = entityType === "lead"
          ? `${API}/whatsapp/sync-lead-history/${id}?phone=${phone}`
          : `${API}/whatsapp/sync-history/${id}?phone=${phone}`;
        await axios.post(url, {});
        sessionStorage.setItem(key, "1");
      } catch { /* best-effort */ }
      setSyncDone(true);
      await fetchMessages();
      setSyncing(false);
    })();
  }, [negocio?.id, entityId, entityType, phone, syncDone, fetchMessages]);

  /* Initial fetch (after sync) */
  useEffect(() => {
    if (!syncDone) return;
    fetchMessages();
  }, [syncDone]); // eslint-disable-line

  // React to SSE whatsapp_message event for instant update (debounced)
  useEffect(() => {
    let debounceTimer = null;
    const onSSE = (e) => {
      const negId = negocio?.id || "";
      const lId = entityType === "lead" ? entityId : "";
      const evtNeg = e.detail?.negocio_id || "";
      const evtLead = e.detail?.lead_id || "";
      if ((negId && evtNeg === negId) || (lId && evtLead === lId)) {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => fetchMessages(), 500);
      }
    };
    window.addEventListener("pipe4:whatsapp_message", onSSE);
    return () => { window.removeEventListener("pipe4:whatsapp_message", onSSE); clearTimeout(debounceTimer); };
  }, [negocio?.id, entityId, entityType, fetchMessages]);

  /* Smart scroll */
  useEffect(() => {
    const newCount = messages.length;
    const prevCount = prevMsgCountRef.current;
    prevMsgCountRef.current = newCount;
    if (newCount === 0) return;
    if (prevCount === 0 || (newCount > prevCount && isAtBottomRef.current)) {
      bottomRef.current?.scrollIntoView({ behavior: prevCount === 0 ? "auto" : "smooth" });
    }
  }, [messages]);

  /* Send text — optimistic */
  const sendText = async () => {
    if (!text.trim() || !phone || sending) return;
    const msgText = text.trim();
    const senderName = authUser?.name || "";
    const displayText = senderName ? `*${senderName}:*\n${msgText}` : msgText;
    const tempId = `temp_${Date.now()}`;
    const optimistic = {
      id: tempId, direction: "outbound", text: msgText, display_text: displayText,
      message_type: "text", created_at: new Date().toISOString(),
      status: "sending", sender_name: senderName, sender_id: authUser?.user_id || "",
    };
    setSending(true);
    setText("");
    setMessages(prev => [...prev, optimistic]);
    try {
      const res = await axios.post(`${API}/whatsapp/send`, {
        negocio_id: leadId ? "" : negocio.id, lead_id: leadId || "", phone, type: "text", text: msgText
      });
      setMessages(prev => prev.map(m => m.id === tempId ? { ...res.data, _optimistic: false } : m));
    } catch {
      setMessages(prev => prev.map(m => m.id === tempId ? { ...m, status: "error" } : m));
    }
    setSending(false);
  };

  /* Helper: register a local object URL for a sent message */
  const addLocalMedia = (msgId, objectUrl) => {
    localMediaRef.current[msgId] = objectUrl;
    setLocalMediaMap(prev => ({ ...prev, [msgId]: objectUrl }));
  };

  /* Send file */
  const sendFile = (file) => {
    const localUrl = URL.createObjectURL(file);
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result.split(",")[1];
      const isImage = file.type.startsWith("image/");
      const isAudio = file.type.startsWith("audio/");
      setSendingFile(true);
      try {
        await axios.post(`${API}/whatsapp/send`, {
          negocio_id: leadId ? "" : negocio.id, lead_id: leadId || "", phone,
          type: isImage ? "image" : isAudio ? "audio" : "document",
          media_base64: base64, filename: file.name, caption: ""
        });
      } catch { /* msg may still have been sent */ }
      await fetchMessages();
      setSendingFile(false);
    };
    reader.readAsDataURL(file);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(); }
  };

  /* Start audio recording */
  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Seu navegador não suporta gravação de áudio.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      audioChunksRef.current = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        if (blob.size < 1000) return; // ignore accidental < 1s recordings
        const localUrl = URL.createObjectURL(blob);
        const reader = new FileReader();
        reader.onload = async (ev) => {
          const base64 = ev.target.result.split(",")[1];
          setSending(true);
          try {
            await axios.post(`${API}/whatsapp/send`, {
              negocio_id: leadId ? "" : negocio.id, lead_id: leadId || "", phone,
              type: "audio", media_base64: base64, filename: "audio.webm"
            });
          } catch { /* msg may still have been sent */ }
          await fetchMessages();
          setSending(false);
        };
        reader.readAsDataURL(blob);
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    } catch {
      toast.error("Permissão de microfone negada.");
    }
  };

  /* Stop audio recording */
  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    clearInterval(recordingTimerRef.current);
    setRecording(false);
    setRecordingTime(0);
  };

  /* Cancel recording without sending */
  const cancelRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = () => {
        mediaRecorderRef.current.stream?.getTracks().forEach(t => t.stop());
      };
      mediaRecorderRef.current.stop();
    }
    clearInterval(recordingTimerRef.current);
    setRecording(false);
    setRecordingTime(0);
  };

  function fmtRecordingTime(secs) {
    const m = Math.floor(secs / 60).toString().padStart(2, "0");
    const s = (secs % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  }

  /* ── Group messages by date ── */
  const msgsByDate = messages.reduce((acc, msg) => {
    const label = fmtDate(msg.created_at);
    if (!acc.length || acc[acc.length - 1].label !== label) {
      acc.push({ label, msgs: [msg] });
    } else {
      acc[acc.length - 1].msgs.push(msg);
    }
    return acc;
  }, []);

  const contactName = contact?.nome || "";

  /* ── Empty states ── */
  if (!contact) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-slate-400 p-8">
        <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
          <Phone className="w-5 h-5 text-slate-400" />
        </div>
        <p className="text-sm font-medium text-slate-600 text-center">Nenhum contato vinculado.</p>
        <p className="text-xs text-center">Vincule um contato na aba "Editar" para habilitar o chat.</p>
      </div>
    );
  }

  if (noPhone) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 text-slate-400 p-8">
        <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
          <Phone className="w-5 h-5 text-slate-400" />
        </div>
        <p className="text-sm font-medium text-slate-600 text-center">Contato sem número de WhatsApp cadastrado.</p>
        <p className="text-xs text-center">Adicione um tipo de contato "Celular" no cadastro do contato.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50">

      {/* ── Header ── */}
      {!hideHeader && (
      <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-slate-200 flex-shrink-0">
        <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold"
          style={{ backgroundColor: tc500 }}>
          {getInitials(contactName)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800 leading-tight truncate">{contactName || contactPhone}</p>
          {/* Phone — dropdown if multiple numbers, plain text if single */}
          {availablePhones.length > 1 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-0.5 text-xs text-slate-400 hover:text-slate-600 transition-colors leading-tight mt-0.5">
                  {selectedPhone?.tipo === "Celular" || selectedPhone?.tipo === "WhatsApp"
                    ? <MessageCircle className="w-3 h-3 mr-0.5 text-emerald-500 flex-shrink-0" />
                    : <Phone className="w-3 h-3 mr-0.5 flex-shrink-0" />}
                  <span>{contactPhone}</span>
                  <ChevronDown className="w-3 h-3 ml-0.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {availablePhones.map((p, i) => (
                  <DropdownMenuItem key={i} onClick={() => setSelectedPhone(p)} className="flex items-center gap-2 cursor-pointer">
                    {p.tipo === "Celular" || p.tipo === "WhatsApp"
                      ? <MessageCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                      : <Phone className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-700 truncate">
                        {(() => { const d = (p.ddi || defaultDdi).replace(/\D/g,""); let l = p.valor.replace(/\D/g,""); if (l.startsWith(d) && l.length > 10) l = l.slice(d.length); return `+${d} ${applyMask(l, phoneMask)}`; })()}
                      </p>
                      <p className="text-[10px] text-slate-400">{p.tipo}</p>
                    </div>
                    {selectedPhone?.valor === p.valor && (
                      <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: tc500 }} />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : contactName && contactPhone ? (
            <p className="text-xs text-slate-400 leading-tight flex items-center gap-0.5 mt-0.5">
              {selectedPhone?.tipo === "Celular" || selectedPhone?.tipo === "WhatsApp"
                ? <MessageCircle className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                : <Phone className="w-3 h-3 flex-shrink-0" />}
              {contactPhone}
            </p>
          ) : null}
        </div>

        {/* Right: typing → sync → synced */}
        {isTyping ? (
          <div className="flex items-center gap-1.5 text-[11px] text-slate-500 flex-shrink-0">
            <div className="flex items-end gap-[3px] h-3">
              {[0, 1, 2].map(i => (
                <div key={i} className="w-1 rounded-full bg-slate-400 animate-bounce"
                  style={{ height: 4 + i * 2, animationDelay: `${i * 150}ms`, animationDuration: "0.8s" }} />
              ))}
            </div>
            <span className="font-medium">
              {typingStatus === "recording" ? "gravando áudio..." : "digitando..."}
            </span>
          </div>
        ) : syncing ? (
          <div className="flex items-center gap-1.5 text-[11px] text-slate-400 flex-shrink-0">
            <svg className="w-3 h-3 animate-spin flex-shrink-0" style={{ color: tc500 }} fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            <span>Sincronizando...</span>
          </div>
        ) : syncDone ? (
          <div className="flex items-center gap-1.5 text-[11px] text-emerald-600 flex-shrink-0">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />
            <span className="font-medium">Sincronizado</span>
          </div>
        ) : null}
      </div>
      )}

      {/* ── Messages ── */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-0.5 relative"
      >
        {/* Sync loading overlay — blocks interaction and shows spinner */}
        {syncing && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/70 backdrop-blur-[2px]">
            <div className="flex flex-col items-center gap-3">
              <svg className="w-8 h-8 animate-spin" style={{ color: tc500 }} fill="none" viewBox="0 0 24 24">
                <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              <p className="text-sm font-medium text-slate-600">Carregando histórico...</p>
              <p className="text-xs text-slate-400">Aguarde enquanto buscamos as mensagens</p>
            </div>
          </div>
        )}
        {!syncing && messages.length === 0 && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center p-6">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                <Phone className="w-5 h-5 text-slate-400" />
              </div>
              <p className="text-sm font-medium text-slate-500">Nenhuma mensagem ainda.</p>
              <p className="text-xs text-slate-400 mt-1">Envie a primeira mensagem abaixo.</p>
            </div>
          </div>
        )}

        {msgsByDate.map(({ label, msgs }) => (
          <div key={label}>
            <DateSeparator label={label} />
            {msgs.map(msg => (
              <MessageBubble key={msg.id} msg={msg} contactName={contactName}
                tc500={tc500} tc50={tc50}
                localMedia={localMediaMap[msg.id]}
                onImageClick={setLightboxUrl}
              />
            ))}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* ── Input area — disabled while syncing ── */}
      <div className={`flex-shrink-0 bg-white border-t border-slate-200 px-3 py-2 ${syncing ? "opacity-50 pointer-events-none select-none" : ""}`}>
        <div className="flex items-end gap-2">

          {/* Attach — spinner while uploading */}
          {!recording && (
            <>
              <button
                type="button"
                onClick={() => !sendingFile && fileInputRef.current?.click()}
                disabled={sending || sendingFile}
                className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-slate-700 hover:border-slate-300 transition-colors flex-shrink-0 disabled:cursor-not-allowed mb-0"
                style={sendingFile ? { borderColor: tc500 } : undefined}
                title={sendingFile ? "Enviando arquivo..." : "Anexar arquivo"}
              >
                {sendingFile ? (
                  <svg className="w-4 h-4 animate-spin" style={{ color: tc500 }} fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                ) : (
                  <Paperclip className="w-4 h-4" />
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={e => e.target.files[0] && sendFile(e.target.files[0])}
                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
              />
            </>
          )}

          {/* Recording indicator OR text input */}
          {recording ? (
            <div className="flex-1 h-9 flex items-center gap-3 px-3 rounded-lg bg-red-50 border border-red-200">
              {/* Cancel recording */}
              <button type="button" onClick={cancelRecording}
                className="text-red-400 hover:text-red-600 transition-colors flex-shrink-0">
                <X className="w-3.5 h-3.5" />
              </button>
              {/* Pulse dot + timer */}
              <div className="flex items-center gap-2 flex-1">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
                <span className="text-sm font-semibold text-red-600 tabular-nums">{fmtRecordingTime(recordingTime)}</span>
                {/* Waveform decoration */}
                <div className="flex items-center gap-0.5 flex-1">
                  {Array.from({ length: 20 }).map((_, i) => (
                    <div key={i} className="w-0.5 rounded-full bg-red-300 animate-pulse"
                      style={{ height: `${6 + Math.sin(i * 0.8) * 5}px`, animationDelay: `${i * 60}ms` }} />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 flex items-end gap-1 overflow-hidden focus-within:border-slate-300 transition-colors">
              <textarea
                value={text}
                onChange={e => handleTextChange(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Digite uma mensagem..."
                rows={1}
                className="w-full text-sm text-slate-700 placeholder-slate-400 resize-none outline-none bg-transparent overflow-hidden leading-relaxed"
                style={{ height: 22, minHeight: 22, maxHeight: 160 }}
                ref={el => { textareaRef.current = el; if (el) { el.style.height = "22px"; const sh = el.scrollHeight; el.style.height = Math.min(sh, 160) + "px"; el.style.overflow = sh > 160 ? "auto" : "hidden"; } }}
                onInput={e => {
                  e.target.style.height = "22px";
                  const sh = e.target.scrollHeight;
                  e.target.style.height = Math.min(sh, 160) + "px";
                  e.target.style.overflow = sh > 160 ? "auto" : "hidden";
                }}
              />
              <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
                <PopoverTrigger asChild>
                  <button type="button" className="flex-shrink-0 text-slate-400 hover:text-slate-600 transition-colors"><Smile className="w-5 h-5" /></button>
                </PopoverTrigger>
                <PopoverContent side="top" align="end" className="w-auto p-0 border-0 shadow-xl" sideOffset={8}>
                  <EmojiPicker
                    onEmojiClick={(emojiData) => {
                      setText(prev => prev + emojiData.emoji);
                      setEmojiOpen(false);
                      setTimeout(() => textareaRef.current?.focus(), 50);
                    }}
                    width={280} height={350}
                    searchPlaceholder="Buscar emoji..."
                    previewConfig={{ showPreview: false }}
                    lazyLoadEmojis
                    emojiStyle="native"
                    skinTonesDisabled
                    categories={[
                      { category: "suggested", name: "Recentes" },
                      { category: "smileys_people", name: "Rostos" },
                      { category: "animals_nature", name: "Animais" },
                      { category: "food_drink", name: "Comida" },
                      { category: "travel_places", name: "Viagem" },
                      { category: "activities", name: "Atividades" },
                      { category: "objects", name: "Objetos" },
                      { category: "symbols", name: "Símbolos" },
                      { category: "flags", name: "Bandeiras" },
                    ]}
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}

          {/* Right button: Send (text) | Stop (recording) | Mic (idle) */}
          {recording ? (
            <button
              type="button"
              onClick={stopRecording}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-red-500 hover:bg-red-600 text-white transition-all flex-shrink-0"
              title="Enviar áudio"
            >
              <Send className="w-4 h-4" />
            </button>
          ) : text.trim() ? (
            <button
              type="button"
              onClick={sendText}
              disabled={sending}
              className="w-9 h-9 flex items-center justify-center rounded-lg text-white transition-all flex-shrink-0 disabled:opacity-40"
              style={{ backgroundColor: tc500 }}
            >
              <Send className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={startRecording}
              disabled={sending}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-slate-700 hover:border-slate-300 transition-colors flex-shrink-0 disabled:opacity-50"
              title="Gravar áudio"
            >
              <Mic className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* ── Lightbox ── */}
      {lightboxUrl && (
        <Dialog open={true} onOpenChange={() => setLightboxUrl(null)}>
          <DialogContent className="max-w-[95vw] max-h-[95vh] !p-0 !border-0 bg-transparent shadow-none [&>button]:hidden">
            <div className="relative flex items-center justify-center w-full h-[90vh]">
              <button onClick={() => setLightboxUrl(null)}
                className="absolute top-2 right-2 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-lg hover:scale-110 transition-all z-10"
                style={{ color: tc500 }}>
                <X className="w-5 h-5" />
              </button>
              <img src={lightboxUrl} alt="imagem"
                className="max-h-[88vh] max-w-[93vw] object-contain rounded-lg" />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
